import React, { useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { detectFallbackLabel } from '@/features/tasks/utils/rejectionLabel';

/**
 * Inline guidance for the single-label classification "Other" (rejection)
 * fallback label, shown beneath the label-review table.
 *
 * Two states:
 *   - Smart detection: when a label named Other/None/Else/Unknown exists and
 *     nothing is selected yet, suggest marking it as the fallback (CTA), with a
 *     dismiss action.
 *   - Empty state: when no label is selected, explain the optional fallback.
 *
 * Renders nothing once a fallback label is selected.
 *
 * @param {Object} props
 * @param {Array<string>} props.extractedLabels
 * @param {Object} props.labelMappings
 * @param {string|null} props.rejectionLabelName
 * @param {(name: string|null) => void} props.onSetRejectionLabel
 */
const RejectionLabelNotice = ({
    extractedLabels = [],
    labelMappings = {},
    rejectionLabelName = null,
    onSetRejectionLabel,
}) => {
    const [dismissed, setDismissed] = useState(false);

    if (rejectionLabelName) {
        return null;
    }

    if (!Array.isArray(extractedLabels) || extractedLabels.length === 0) {
        return null;
    }

    const detected = detectFallbackLabel(extractedLabels, labelMappings);

    if (detected && !dismissed) {
        return (
            <Alert
                type="info"
                showIcon
                style={{ marginTop: 12, alignItems: 'flex-start', fontSize: 12 }}
                message={
                    <div>
                        <span>
                            <b>{detected}</b> is commonly used as a catch-all
                            fallback. Consider marking it as &quot;Other&quot;
                            in the column above.
                        </span>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: 8,
                            }}
                        >
                            <Space size={8}>
                                <Button
                                    size="small"
                                    type="primary"
                                    style={{
                                        fontSize: 12,
                                        height: 22,
                                        padding: '0 8px',
                                    }}
                                    onClick={() => {
                                        if (typeof onSetRejectionLabel === 'function') {
                                            onSetRejectionLabel(detected);
                                        }
                                    }}
                                >
                                    Set as &quot;Other&quot;
                                </Button>
                                <Button
                                    size="small"
                                    type="text"
                                    style={{
                                        fontSize: 12,
                                        height: 22,
                                        padding: '0 8px',
                                    }}
                                    onClick={() => setDismissed(true)}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </div>
                    </div>
                }
            />
        );
    }

    return (
        <div
            style={{
                marginTop: 12,
                color: '#8c8c8c',
                fontSize: 13,
                lineHeight: 1.5,
            }}
        >
            No &quot;Other&quot; label selected. Selecting an optional &quot;Other&quot;
            label gives your model a fallback category for uncertain predictions.
            You can continue without one.
        </div>
    );
};

export default RejectionLabelNotice;
